import React, { useCallback, useRef, useState, useEffect } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  FiPaperclip,
  FiSliders,
  FiCpu,
  FiCheck,
  FiEye,
  FiLoader,
  FiPause,
} from 'react-icons/fi';
import {
  FaRegLightbulb,
  FaGlobeAsia,
  FaFileUpload,
  FaClock,
} from 'react-icons/fa';
import { RxCross2 } from 'react-icons/rx';
import toast from 'react-hot-toast';
import { IoSend, IoChevronUp } from 'react-icons/io5';
import { FiDownload } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { BsPuzzle, BsRobot } from 'react-icons/bs';
import { motion } from 'framer-motion';
import { TbPlugConnected } from 'react-icons/tb';
import { RiClaudeFill, RiGeminiFill } from 'react-icons/ri';
import { TbBrandOpenai } from 'react-icons/tb';

import { DataState } from '../context/DataProvider';
import {
  deleteFile,
  downloadFile,
  uploadFilesFromDriveLink,
  API_URL,
} from '../services/api';
import Loader from './custom/Loader';
import FileManagement from './FileManagement';
import { integrationData, triggers } from '../utils/constants';
import SelectFunctions from './modals/SelectFunstions';
import GroupsModal from './modals/GroupsModal';
import DataSourceModal from './DataSourceModal';
import DataApps from './DataSource/DataApps';
import ImageComponent from './ImageComponent';
import { getFileIcon } from './chat/AttachmentIcon';
import McpTaskSelector from './McpConnections/McpTaskSelector';
import AgentArchitectureModal from './modals/AgentArchitectureModal';
import ExcelPreviewModal from './chat/modals/ExcelPreviewModal';
import DocxPreviewModal from './chat/PreviewModal';
import IntegrationGuideNotification from './integrations/IntegrationGuideNotification';
import useClickOutside from '../hooks/useClickOutside';
import Integration from './Integration';

const IN_PRODUCTION = process.env.REACT_APP_IS_PROD === 'true';

//Stop chat response btn
function LoadingPauseIcon() {
  return (
    <div className="relative flex items-center justify-center">
      <div
        className="relative flex flex-none items-center justify-center"
        style={{ width: 24, height: 24 }}
      >
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-zinc-600 border-t-zinc-300"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
        />
        <FiPause className="z-10 h-4 w-4 text-zinc-100" />
      </div>
    </div>
  );
}

export const getFileForPreview = async (accessToken, fileId) => {
  const options = {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: 'include',
  };

  try {
    const response = await fetch(
      `${API_URL}/file/download-direct/${fileId}`,
      options,
    );

    if (response.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
      return;
    }

    if (!response.ok) {
      throw new Error(`Error fetching file: ${response.statusText}`);
    }

    return response.blob();
  } catch (error) {
    throw error;
  }
};

const getModelIcon = (name) => {
  const lower = name.toLowerCase();

  if (/claude/i.test(lower))
    return <RiClaudeFill className="text-base text-zinc-400" />;

  if (/gpt|openai/i.test(lower))
    return <TbBrandOpenai className="text-base text-zinc-400" />;

  if (/gemini/i.test(lower))
    return <RiGeminiFill className="text-base text-zinc-400" />;

  if (/oqlous/i.test(lower))
    return (
      <img
        alt="oqlous icon"
        className="h-4 w-4 text-base text-zinc-400"
        src="../assets/logo.png"
      />
    );

  if (/deepseek/i.test(lower))
    return (
      <img
        alt="deepseek icon"
        className="h-4 w-4 text-base text-zinc-400"
        src="../assets/deepseek.svg"
      />
    );

  if (/glm|zhipu/i.test(lower))
    return (
      <img
        alt="glm icon"
        className="h-4 w-4 text-base text-zinc-400"
        src="../assets/glm.svg"
      />
    );

  return null;
};

const AutoResizableTextarea = ({
  setInput,
  input,
  handleSend,
  isDraggingOver = false,
  tempImages = [],
  setTempImages = () => {},
  justUploadedImages = false,
  setJustUploadedImages = () => {},
  processAndUploadFiles = () => {},
  newChat = false,
}) => {
  const {
    showUploadModal,
    setShowUploadModal,
    files,
    setFiles,
    userGoogleId,
    createTaskLoading,
    sendTaskLoading,
    sendSubTaskLoading,
    isBusy,
    currentTaskId,
    justSavedFiles,
    setJustSavedFiles,
    isReasoning,
    setIsReasoning,
    isWebSearch,
    setIsWebSearch,
    isCanvas,
    isAgentMode,
    setIsAgentMode,
    taskMCPConnections,
    taskOqlousTools,
    taskManualToolSelection,
    currentTaskModel,
    setCurrentTaskModel,
    toolCallApprovalList,
    userInfo,
    stopStreaming,
    isStreaming,
    hasPendingToolCalls,
  } = DataState();

  const location = useLocation();

  //states
  const [isMcpSelectorOpen, setIsMcpSelectorOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isloading, setIsloading] = useState(false);
  const [showMobileOptions, setShowMobileOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isModelSelectorOpen, setIsModelSelectorOpen] = useState(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [processingFileId, setProcessingFileId] = useState(null);
  const [selectedValue, setSelectedValue] = useState(integrationData[2]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeTrigger, setActiveTrigger] = useState(null);
  const [activeTriggerInfo, setActiveTriggerInfo] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [excelPreviewModal, setExcelPreviewModal] = useState({
    isOpen: false,
    file: null,
  });
  const [showGroups, setShowGroups] = useState(false);
  const [driveLinks, setDriveLinks] = useState([]);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  //refs
  const modelSelectorRef = useRef(null);
  const walletAddressRef = useRef(null);
  const mobileOptionsRef = useRef(null);
  const textareaRef = useRef(null);
  const debounceTimeoutRef = useRef(null);
  const scrollRef = useRef(null);
  const DEBOUNCE_DELAY = 500;
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  //custom hooks
  useClickOutside([mobileOptionsRef], () => setShowMobileOptions(false));
  useClickOutside([walletAddressRef], () => setShowUploadModal(false));
  useClickOutside([modelSelectorRef], () => setIsModelSelectorOpen(false));

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  // Inside your AutoResizableTextarea component
  const handleTurnOffAgentMode = (e) => {
    // This prevents the main button's onClick from firing as well
    e.stopPropagation();
    setIsAgentMode(false);
    toast('Agent Mode Deactivated', {
      icon: '🤖',
      style: {
        borderRadius: '10px',
        background: '#333',
        color: '#fff',
      },
    });
  };

  // 3. Create a handler to open the agent modal
  const handleAgentButtonClick = () => {
    if (isAgentMode) {
      // If agent mode is already on, open the modal for control
      setIsAgentModalOpen(true);
    } else {
      // Otherwise, just turn agent mode on
      setIsAgentMode(true);
      toast.success('Agent Mode Activated');
    }
  };

  const onDragStart = (e) => {
    setIsDragging(true);
    scrollRef.current.classList.add('cursor-grabbing');
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const onDragMove = (e) => {
    if (!isDragging) return;
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // scroll speed multiplier
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const onDragEnd = () => {
    setIsDragging(false);
    scrollRef.current.classList.remove('cursor-grabbing');
  };

  const [showDataSource, setShowDataSource] = useState(false);
  const closeDataSourceModal = () => {
    setShowDataSource(false);
  };

  // Regex to match Google Drive and Google Docs URLs
  const GOOGLE_DRIVE_URL_REGEX =
    /https?:\/\/(?:drive\.google\.com\/(?:file\/d\/|open\?id=|uc\?(?:export=download&)?id=)|docs\.google\.com\/(?:document|spreadsheets|presentation)\/d\/)[^\s]+/gi;

  // Download files from Google Drive links
  const handleDriveLinksDownload = async (urls) => {
    const newLinks = urls.map((url, i) => ({
      id: Date.now() + i,
      url,
      status: 'downloading',
      filename: null,
    }));
    setDriveLinks((prev) => [...prev, ...newLinks]);

    try {
      const response = await uploadFilesFromDriveLink(
        userGoogleId,
        currentTaskId,
        urls,
      );

      const filesInfo = response?.files_info || [];
      setDriveLinks((prev) =>
        prev.map((link) => {
          const match = newLinks.find((nl) => nl.id === link.id);
          if (match) {
            const fileInfo = filesInfo.find((f) => f.file_id || f.filename);
            return {
              ...link,
              status: 'done',
              filename:
                fileInfo?.original_filename || fileInfo?.filename || 'File',
            };
          }
          return link;
        }),
      );

      toast.success(`${filesInfo.length} file(s) downloaded from Google Drive`);
      setJustSavedFiles(true);

      // Auto-remove done chips after 3 seconds
      setTimeout(() => {
        setDriveLinks((prev) =>
          prev.filter((l) => !newLinks.find((nl) => nl.id === l.id)),
        );
      }, 3000);
    } catch (err) {
      setDriveLinks((prev) =>
        prev.map((link) =>
          newLinks.find((nl) => nl.id === link.id)
            ? { ...link, status: 'error' }
            : link,
        ),
      );
      const errorMsg =
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to download from Google Drive';
      toast.error(errorMsg);
    }
  };

  // UPDATED: handlePaste now detects Google Drive links and also handles files
  const handlePaste = (event) => {
    const clipboardFiles = event.clipboardData.files;
    if (clipboardFiles && clipboardFiles.length > 0) {
      event.preventDefault();
      processAndUploadFiles(clipboardFiles);
      return;
    }

    // Check for Google Drive URLs in pasted text
    const pastedText = event.clipboardData.getData('text');
    if (pastedText) {
      const driveUrls = pastedText.match(GOOGLE_DRIVE_URL_REGEX);
      if (driveUrls && driveUrls.length > 0) {
        event.preventDefault();
        // Remove the Drive URLs from the text and keep the rest
        let remainingText = pastedText;
        driveUrls.forEach((url) => {
          remainingText = remainingText.replace(url, '').trim();
        });
        if (remainingText) {
          setInputValue((prev) => prev + remainingText);
          // setInput(prev => prev + remainingText);
        }
        handleDriveLinksDownload(driveUrls);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (
      !isloading &&
      !createTaskLoading &&
      !sendTaskLoading &&
      !sendSubTaskLoading &&
      !isBusy &&
      !((toolCallApprovalList?.length || 0) > 0) &&
      e.key === 'Enter' &&
      !e.shiftKey &&
      !e.ctrlKey &&
      !e.altKey &&
      !e.metaKey
    ) {
      e.preventDefault(); // Prevent the default newline insert
      // Prepend template context if a template is selected
      let messageToSend = inputValue;
      if (selectedTemplate) {
        messageToSend = `[Using template: "${selectedTemplate.template_name}" (template_id: ${selectedTemplate._id})]\n${inputValue}`;
        setSelectedTemplate(null);
      }

      handleSend(messageToSend);

      // ✅ update global ONLY here
      // setInput(messageToSend);
      setInputValue('');
      setInput('');
    }
  };

  const handleDownload = async (fileId) => {
    // +++ Set the processing ID at the start +++
    setProcessingFileId(fileId);
    try {
      const response = await downloadFile(fileId, userGoogleId);
      if (!response.error) {
        window.open(response.url, '_blank');
      }
    } catch (error) {
      toast.error('Error downloading file');
    } finally {
      // +++ Clear the processing ID at the end +++
      setProcessingFileId(null);
    }
  };

  const handleDelete = async (fileId) => {
    // +++ Set the processing ID at the start +++
    setProcessingFileId(fileId);
    try {
      const response = await deleteFile(fileId, userGoogleId);
      if (response.error) {
        toast.error(response.error);
      } else {
        toast.success('File deleted successfully');
        // eslint-disable-next-line
        setFiles(files.filter((file) => file.file_id != fileId));
      }
    } catch (error) {
      toast.error('Error deleting file');
    } finally {
      // +++ Clear the processing ID at the end +++
      setProcessingFileId(null);
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInputValue(value);
    // setInput(value);

    // Get the word that contains the cursor position
    const cursorPosition = event.target.selectionStart;
    const words = value.slice(0, cursorPosition).split(' ');
    const currentWord = words[words.length - 1];

    const textBeforeCursor = value.slice(0, cursorPosition);
    const lastSpaceIndex = textBeforeCursor.lastIndexOf(' ');
    const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
    const wordSegmentStartIndex =
      Math.max(lastSpaceIndex, lastNewlineIndex) + 1;

    const currentWordSegment = textBeforeCursor.slice(wordSegmentStartIndex);

    // Trim only leading spaces from the current segment to find the actual word start
    const trimmedWordAtCursor = currentWordSegment.trimStart();

    const matchedTrigger = Object.keys(triggers)
      .sort((a, b) => b.length - a.length)
      .find((trigger) => trimmedWordAtCursor.startsWith(trigger));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (matchedTrigger && trimmedWordAtCursor.length >= matchedTrigger.length) {
      const actualWordStartIndex =
        wordSegmentStartIndex +
        (currentWordSegment.length - trimmedWordAtCursor.length);

      setActiveTrigger(matchedTrigger);
      setActiveTriggerInfo({
        startIndex: actualWordStartIndex,
        originalWordText: trimmedWordAtCursor,
        trigger: matchedTrigger,
      });

      // Calculate dropdown position relative to viewport
      if (textareaRef.current) {
        const rect = textareaRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.top - 8, // 8px gap above textarea
          left: rect.left,
        });
      }
      const query = currentWord
        .trim()
        .slice(matchedTrigger.length)
        .toLowerCase();

      const triggerConfig = triggers[matchedTrigger];

      if (typeof triggerConfig.data === 'function') {
        if (query.length >= 0) {
          debounceTimeoutRef.current = setTimeout(() => {
            // Call your performApiSuggestionFetch, which internally calls the configured function
            performApiSuggestionFetch(
              userGoogleId,
              query, // Pass raw query
              triggerConfig.data, // Pass the fetch function from config
            );
          }, DEBOUNCE_DELAY);
        } else {
          // Query is empty/too short for API call, clear suggestions
          setFilteredData([]);
          setShowDropdown(false);
        }
      } else {
        const lowercaseQuery = query.toLowerCase(); // Use lowercase for static filtering
        const filtered = triggerConfig.data.filter((item) => {
          const searchValue = String(
            item[triggerConfig.mainField],
          ).toLowerCase();
          return searchValue.includes(lowercaseQuery);
        });
        setFilteredData(filtered);
        setShowDropdown(filtered.length > 0);
      }
    } else {
      setActiveTrigger(null);
      setShowDropdown(false);
      setActiveTriggerInfo(null);
      setFilteredData([]);
    }
  };

  const handleItemClick = (item) => {
    if (!activeTriggerInfo || !activeTrigger) {
      setShowDropdown(false);
      return;
    }

    const { startIndex, originalWordText } = activeTriggerInfo;
    const triggerConfig = triggers[activeTrigger];
    const selectedValue = item[triggerConfig.mainField];

    // For report-template type, store as a capsule chip instead of inline text
    if (triggerConfig.type === 'report-template' && item._id) {
      setSelectedTemplate({ _id: item._id, template_name: selectedValue });
      // Remove the trigger text from textarea
      const prefix = inputValue.slice(0, startIndex);
      const suffix = inputValue.slice(startIndex + originalWordText.length);
      const finalNewValue = `${prefix}${suffix}`.trim();
      setInputValue(finalNewValue);
      // setInput(finalNewValue);
      setShowDropdown(false);
      setActiveTrigger(null);
      setActiveTriggerInfo(null);
      if (textareaRef.current) textareaRef.current.focus();
      return;
    }

    const prefix = inputValue.slice(0, startIndex);
    const suffix = inputValue.slice(startIndex + originalWordText.length);

    const finalNewValue = `${prefix}${selectedValue} ${suffix}`;
    setInputValue(finalNewValue);
    // setInput(finalNewValue);
    setShowDropdown(false);
    setActiveTrigger(null);
    setActiveTriggerInfo(null);

    // Focus the textarea and set the cursor position correctly
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          const newCursorPos = prefix.length + selectedValue.length + 1;
          textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      }, 0);
    }
  };

  const performApiSuggestionFetch = useCallback(
    async (token, queryForApi, fetchFunction) => {
      setIsloading(true);
      try {
        const suggestionsResultResponse = await fetchFunction(
          token,
          queryForApi,
        );
        let suggestionsResult = [];
        if (Array.isArray(suggestionsResultResponse)) {
          suggestionsResult = suggestionsResultResponse;
        } else if (suggestionsResultResponse?.suggestions) {
          suggestionsResult = suggestionsResultResponse.suggestions;
        } else if (
          suggestionsResultResponse?.data &&
          Array.isArray(suggestionsResultResponse.data)
        ) {
          suggestionsResult = suggestionsResultResponse.data;
        }

        if (Array.isArray(suggestionsResult)) {
          setFilteredData(suggestionsResult);
          setShowDropdown(suggestionsResult.length > 0);
        } else {
          setFilteredData([]);
          setShowDropdown(false);
          if (suggestionsResult !== undefined) {
          }
        }
      } catch (error) {
        setFilteredData([]);
        setShowDropdown(false);
      } finally {
        setIsloading(false);
      }
    },
    [setIsloading, setFilteredData, setShowDropdown],
  );

  const renderDropdownItem = (item, index) => {
    if (!activeTrigger) return null;

    const triggerConfig = triggers[activeTrigger];
    const mainValue = item[triggerConfig.mainField];

    // Custom UI based on trigger type
    const renderContent = () => {
      switch (triggerConfig.type) {
        case 'slack-user':
          return (
            <div className="flex flex-col">
              <span className="font-medium">@{mainValue}</span>
              <span className="text-xs text-zinc-400">{item.RealName}</span>
            </div>
          );
        case 'slack-channel':
          return (
            <div className="flex flex-col">
              <span className="font-medium">#{mainValue}</span>
              <span className="max-w-md truncate text-xs text-zinc-400">
                {item.Members} members
              </span>
            </div>
          );

        case 'jira-sprint':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.State} • {item.StartDate} to {item.EndDate}
              </span>
            </div>
          );

        case 'jira-board':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.ProjectKey} • {item.Type}
              </span>
            </div>
          );

        case 'repository':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.Collaborators?.length || 0} collaborators
              </span>
            </div>
          );

        case 'collaborator':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
            </div>
          );

        case 'jira-user':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">{item.email}</span>
            </div>
          );
        case 'confluence-space':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{item.name}</span>
              <span className="text-xs text-zinc-400">{item.key}</span>
            </div>
          );

        case 'report-template':
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              <span className="text-xs text-zinc-400">
                {item.template_type} • {item.section_count} sections
              </span>
            </div>
          );

        default:
          return (
            <div className="flex flex-col">
              <span className="font-medium">{mainValue}</span>
              {triggerConfig.displayFields.map((field) => (
                <span key={field} className="text-xs text-zinc-400">
                  {item[field]}
                </span>
              ))}
            </div>
          );
      }
    };

    return (
      <li key={index}>
        <button
          onClick={() => handleItemClick(item)}
          className="flex w-full items-center gap-2 px-6 py-3 text-sm hover:bg-zinc-700"
        >
          {renderContent()}
        </button>
      </li>
    );
  };

  // Mobile Tools Component
  const MobileToolsDropup = () => (
    <div
      ref={mobileOptionsRef}
      className="absolute bottom-full left-0 z-50 mb-2 min-w-[180px] rounded-lg border border-zinc-700 bg-zinc-800 p-2 shadow-lg"
    >
      <div className="flex flex-col gap-1">
        <button
          disabled={sendTaskLoading}
          onClick={() => {
            setIsReasoning(!isReasoning);
            setShowMobileOptions(false);
          }}
          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 ${
            isReasoning
              ? 'border border-primary_c bg-primary_c text-white'
              : 'text-zinc-300 hover:bg-zinc-700'
          }`}
        >
          <FaRegLightbulb className="text-sm" />
          <span>Reason</span>
        </button>
        <button
          disabled={sendTaskLoading}
          onClick={() => {
            setIsWebSearch(!isWebSearch);
            setShowMobileOptions(false);
          }}
          className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-all duration-200  disabled:pointer-events-none disabled:opacity-50 ${
            isWebSearch
              ? 'border border-primary_c bg-primary_c text-white'
              : 'text-zinc-300 hover:bg-zinc-700'
          }`}
        >
          <FaGlobeAsia className="text-sm" />
          <span>Web</span>
        </button>
        {!IN_PRODUCTION && (
          <div className="relative">
            {' '}
            {/* Wrap in relative container */}
            <button
              disabled={sendTaskLoading}
              onClick={() => {
                handleAgentButtonClick();
                setShowMobileOptions(false);
              }}
              className={`flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm transition-all duration-200  disabled:pointer-events-none disabled:opacity-50 ${
                isAgentMode
                  ? 'border border-primary_c bg-primary_c text-white'
                  : 'text-zinc-300 hover:bg-zinc-700'
              }`}
            >
              <div className="flex items-center gap-2">
                <BsRobot className="text-sm" />
                <span>Agents</span>
              </div>
            </button>
            {/* The 'X' button for mobile */}
            {isAgentMode && (
              <button
                disabled={sendTaskLoading}
                onClick={(e) => {
                  handleTurnOffAgentMode(e);
                  setShowMobileOptions(false);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/20 p-1 transition-colors hover:bg-black/40  disabled:pointer-events-none disabled:opacity-50 "
                title="Turn off Agent Mode"
              >
                <RxCross2 size={16} />
              </button>
            )}
          </div>
        )}
        {/* MODIFIED: Mobile Model Selector with descriptions */}
        {userInfo?.available_models?.length > 0 && (
          <>
            <div className="my-1 border-t border-zinc-700" />
            <div className="relative" ref={modelSelectorRef}>
              <button
                disabled={sendTaskLoading}
                onClick={() => setIsModelSelectorOpen(!isModelSelectorOpen)}
                className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm text-zinc-300 transition-all duration-200 hover:bg-zinc-700 disabled:pointer-events-none disabled:opacity-50"
              >
                <div className="flex items-center gap-2">
                  <FiCpu className="text-sm" />
                  <span>{currentTaskModel || 'Model'}</span>
                </div>
                <IoChevronUp
                  className={`text-sm transition-transform duration-200 ${isModelSelectorOpen ? 'rotate-0' : 'rotate-180'}`}
                />
              </button>
              {isModelSelectorOpen && (
                <div className="absolute bottom-0 left-full z-50 ml-2 w-64 rounded-lg border border-zinc-700 bg-zinc-900 shadow-lg">
                  <ul className="max-h-60 overflow-y-auto">
                    <li
                      title={
                        userInfo?.default_model?.description ||
                        'The default Oqlous model.'
                      }
                    >
                      <button
                        onClick={() => {
                          setCurrentTaskModel(null);
                          setIsModelSelectorOpen(false);
                          setShowMobileOptions(false);
                        }}
                        className={`flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-zinc-700 ${currentTaskModel === null ? 'text-blue-400' : 'text-zinc-300'}`}
                      >
                        <div className="flex flex-col">
                          <span
                            className={`text-xs font-semibold ${currentTaskModel === null ? 'text-blue-400' : 'text-zinc-200'}`}
                          >
                            {userInfo?.default_model?.name || 'Oqlous'}
                          </span>
                          {userInfo?.default_model?.description && (
                            <span className="mt-0.5 whitespace-pre-wrap text-xs font-normal text-zinc-400">
                              {userInfo.default_model.description}
                            </span>
                          )}
                        </div>
                        {currentTaskModel === null && (
                          <FiCheck className="self-center text-blue-400" />
                        )}
                      </button>
                    </li>
                    {userInfo.available_models.map((model, index) => (
                      <li key={model.name || index} title={model.description}>
                        <button
                          onClick={() => {
                            setCurrentTaskModel(model.name);
                            setIsModelSelectorOpen(false);
                            setShowMobileOptions(false);
                          }}
                          className={`flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-zinc-700 ${currentTaskModel === model.name ? 'text-blue-400' : 'text-zinc-300'}`}
                        >
                          <div className="flex flex-col">
                            {/* <span className={`font-semibold text-xs ${currentTaskModel === model.name ? 'text-blue-400' : 'text-zinc-200'}`}>{model.name}</span> */}
                            {/* --- MODIFICATION START --- */}
                            <div className="flex items-center">
                              <span
                                className={`text-xs font-semibold ${currentTaskModel === model.name ? 'text-blue-400' : 'text-zinc-200'}`}
                              >
                                {model.name}
                              </span>
                              {model.is_new && (
                                // --- MODIFICATION START ---
                                <span className="-mt-px ml-2 inline-flex items-center rounded-md border border-yellow-500 px-1 text-xs font-medium text-yellow-500">
                                  NEW
                                </span>
                                // --- MODIFICATION END ---
                              )}
                            </div>
                            {/* --- MODIFICATION END --- */}
                            {model.description && (
                              <span className="mt-0.5 whitespace-pre-wrap text-xs font-normal text-zinc-400">
                                {model.description}
                              </span>
                            )}
                          </div>
                          {currentTaskModel === model.name && (
                            <FiCheck className="self-center text-blue-400" />
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );

  //Excel preview handler
  const handleExcelPreview = async (file) => {
    setProcessingFileId(file.file_id);

    try {
      const blob = await getFileForPreview(userGoogleId, file.file_id);

      const fileObject = new File([blob], file.filename, {
        type:
          blob.type ||
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      setExcelPreviewModal({
        isOpen: true,
        file: fileObject,
      });
    } catch (error) {
      toast.error('Could not load preview.');
    } finally {
      setProcessingFileId(null);
    }
  };

  const closeExcelPreview = () => {
    setExcelPreviewModal({
      isOpen: false,
      file: null,
    });
  };

  //Docx preview handler
  const handleDocxPreview = async (file) => {
    // +++ Set the processing ID at the start +++
    setProcessingFileId(file.file_id);
    try {
      const blob = await getFileForPreview(userGoogleId, file.file_id);
      const fileObject = new File([blob], file.filename, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      setPreviewFile(fileObject);
      setIsPreviewOpen(true);
    } catch (error) {
      toast.error('Could not load preview.');
    } finally {
      // +++ Clear the processing ID at the end +++
      setProcessingFileId(null);
    }
  };

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    setInputValue(input || '');
  }, [input]);

  useEffect(() => {
    if (newChat) {
      setInputValue('');
    }
  }, [newChat]);

  return (
    <>
      <div className="relative mx-auto flex w-full max-w-[750px] flex-col gap-2 rounded-2xl border border-zinc-700 bg-zinc-800">
        <div className="relative mx-auto flex w-full max-w-[750px] flex-col gap-2 rounded-2xl border border-zinc-700 bg-white bg-opacity-5">
          {/* Image display */}
          <ImageComponent
            tempImages={tempImages}
            justUploadedImages={justUploadedImages}
            setJustUploadedImages={setJustUploadedImages}
          />

          {/* Google Drive link download chips */}
          {driveLinks.length > 0 && (
            <div className="flex flex-wrap gap-2 px-2 pb-1 pt-2">
              {driveLinks.map((link) => (
                <div
                  key={link.id}
                  className={`inline-flex h-[36px] items-center gap-2 rounded-lg border px-3 py-1 text-xs backdrop-blur-sm transition-all duration-200
                  ${
                    link.status === 'downloading'
                      ? 'border-blue-600/40 bg-blue-900/30 text-blue-300'
                      : link.status === 'done'
                        ? 'border-green-600/40 bg-green-900/30 text-green-300'
                        : 'border-red-600/40 bg-red-900/30 text-red-300'
                  }`}
                >
                  <img
                    src="https://res.cloudinary.com/dsguoq6ad/image/upload/v1726229007/mentorEducationImages/lhupskidmwtibynwa0no.png"
                    className="h-4 w-4 flex-shrink-0"
                    alt="Google Drive"
                  />
                  <span className="max-w-[180px] truncate">
                    {link.status === 'downloading'
                      ? 'Downloading from Drive...'
                      : link.status === 'done'
                        ? link.filename
                        : 'Download failed'}
                  </span>
                  {link.status === 'downloading' && (
                    <FiLoader className="h-3 w-3 flex-shrink-0 animate-spin" />
                  )}
                  {link.status === 'done' && (
                    <FiCheck className="h-3 w-3 flex-shrink-0" />
                  )}
                  {link.status === 'error' && (
                    <button
                      onClick={() =>
                        setDriveLinks((prev) =>
                          prev.filter((l) => l.id !== link.id),
                        )
                      }
                      className="transition-colors hover:text-red-200"
                    >
                      <RxCross2 className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Files display */}
          {files?.length > 0 && (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <ul
              ref={scrollRef}
              className="no-scrollbar flex gap-2 overflow-x-auto whitespace-nowrap px-2 pb-1 pt-2"
              onMouseDown={onDragStart}
              onMouseUp={onDragEnd}
              onMouseMove={onDragMove}
              onMouseLeave={onDragEnd}
            >
              {files.map((file) => {
                const filename = file.filename.toLowerCase();
                const isDocx = filename.endsWith('.docx');
                const isExcel =
                  filename.endsWith('.xlsx') ||
                  filename.endsWith('.xls') ||
                  filename.endsWith('.csv');
                const isPreviewable = isDocx || isExcel;
                const isProcessingThisFile = processingFileId === file.file_id;
                const isAnyFileProcessing = processingFileId !== null;

                return (
                  <li
                    key={file.file_id}
                    className="
                    group relative
                    inline-block flex h-[52px]
                    min-w-[200px] max-w-[260px] flex-shrink-0 
                    items-center gap-2 rounded-lg
                    border border-zinc-700/50 bg-zinc-800/60
                    px-3
                    py-2 shadow-sm 
                    backdrop-blur-sm transition-all
                    duration-200 hover:border-zinc-600/50
                    hover:bg-zinc-700/60 hover:shadow-md
                  "
                  >
                    {/* Close button */}
                    <button
                      onClick={() => handleDelete(file.file_id)}
                      className="
                      absolute -right-1.5 -top-1.5 
                      z-10 rounded-full
                      bg-zinc-600 p-1
                      text-zinc-200 opacity-0
                      shadow-md transition-all
                      duration-200 hover:bg-zinc-500
                      hover:text-white hover:shadow-lg
                      group-hover:opacity-100
                    "
                      aria-label="Remove file"
                    >
                      <RxCross2 className="h-3 w-3" />
                    </button>

                    {/* File icon with background */}
                    <div
                      className="
                      flex 
                      h-9 
                      w-9 
                      flex-shrink-0 
                      items-center justify-center rounded-md 
                      bg-zinc-700/50 p-1.5
                      transition-colors
                      duration-200 group-hover:bg-zinc-600/50
                    "
                    >
                      <span className="text-lg text-zinc-300">
                        {getFileIcon(file.original_filename)}
                      </span>
                    </div>

                    {/* File details */}
                    <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5">
                      <span className="truncate text-xs font-medium leading-tight text-zinc-100">
                        {file?.filename || file?.original_filename}
                      </span>
                      <span className="text-[10px] font-normal text-zinc-400">
                        {file?.size
                          ? `${(file.size / 1024).toFixed(1)} KB`
                          : '-- KB'}
                      </span>
                    </div>

                    {/* Action button - Shows on hover */}
                    <div
                      className="
                    flex-shrink-0 
                    opacity-0 transition-opacity 
                    duration-200 group-hover:opacity-100
                  "
                    >
                      {isPreviewable ? (
                        <button
                          onClick={() => {
                            if (isDocx) {
                              handleDocxPreview(file);
                            } else if (isExcel) {
                              handleExcelPreview(file);
                            }
                          }}
                          disabled={isAnyFileProcessing}
                          className="
                          rounded-md border
                          border-zinc-600/30 bg-zinc-700/50
                          p-1.5 text-zinc-300
                          transition-all duration-200
                          hover:bg-zinc-600/50 hover:text-zinc-100
                          disabled:cursor-not-allowed disabled:opacity-40
                        "
                          title={
                            isDocx ? 'Preview Document' : 'Preview Spreadsheet'
                          }
                        >
                          {isProcessingThisFile ? (
                            <FiLoader className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FiEye className="h-3.5 w-3.5" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleDownload(file.file_id)}
                          disabled={isAnyFileProcessing}
                          className="
                          rounded-md border
                          border-zinc-600/30 bg-zinc-700/50
                          p-1.5 text-zinc-300
                          transition-all duration-200
                          hover:bg-zinc-600/50 hover:text-zinc-100
                          disabled:cursor-not-allowed disabled:opacity-40
                        "
                          title="Download File"
                        >
                          {isProcessingThisFile ? (
                            <FiLoader className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <FiDownload className="h-3.5 w-3.5" />
                          )}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="flex w-full flex-col items-center justify-center gap-3 rounded-lg p-4">
            <div className="relative w-full">
              {/* Selected template capsule chip */}
              {selectedTemplate && (
                <div className="mb-2 flex items-center">
                  <div
                    className="inline-flex items-center gap-2 rounded-full border border-[#5652CF]/50 px-3 py-1.5 text-xs font-medium text-white"
                    style={{ backgroundColor: 'rgba(86, 82, 207, 0.3)' }}
                  >
                    <span className="text-[#a5a2f7]">📄</span>
                    <span>{selectedTemplate.template_name}</span>
                    <button
                      onClick={() => setSelectedTemplate(null)}
                      className="ml-1 rounded-full p-0.5 transition-colors hover:bg-white/10"
                      title="Remove template"
                    >
                      <RxCross2 size={12} />
                    </button>
                  </div>
                </div>
              )}
              <TextareaAutosize
                ref={textareaRef}
                minRows={1}
                maxRows={10}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                className="text-p-text min-h-full w-full flex-1 resize-none bg-transparent p-1 pb-0 text-sm outline-none"
                placeholder="Enter Prompt to Execute"
              />
            </div>

            {/* Button Row */}
            <div className="relative mt-3 flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                {showMobileOptions && isMobile && <MobileToolsDropup />}

                {location.pathname === '/chat' && (
                  <button
                    disabled={sendTaskLoading}
                    onClick={() => setShowUploadModal(true)}
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-5xl transition-all duration-300 disabled:pointer-events-none  disabled:opacity-50 ${
                      isloading
                        ? 'bg-opacity-40'
                        : 'hover:bg-gray-300 hover:text-black'
                    }`}
                  >
                    <FiPaperclip className="text-base" />
                  </button>
                )}
                {showUploadModal && (
                  <div
                    ref={walletAddressRef}
                    className="absolute -left-3 bottom-12 z-50"
                  >
                    <div className="bg-p-text relative w-fit rounded-2xl">
                      <div className="side-section">
                        {userGoogleId && (
                          <FileManagement
                            task_id={currentTaskId}
                            userGoogleId={userGoogleId}
                            justSavedFiles={justSavedFiles}
                            setJustSavedFiles={setJustSavedFiles}
                            setJustUploadedImages={setJustUploadedImages}
                            setTempImages={setTempImages}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowIntegrations(true);
                  }}
                  className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-5xl transition-all duration-300 hover:bg-gray-300  hover:text-black disabled:pointer-events-none disabled:opacity-50`}
                >
                  <TbPlugConnected className="text-base" />
                </button>

                {/* MCP button */}
                <div className="relative flex-shrink-0">
                  <button
                    disabled={sendTaskLoading}
                    onClick={() => setIsMcpSelectorOpen(true)}
                    title="Select Connections & Tools"
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-5xl transition-all duration-300 disabled:pointer-events-none  disabled:opacity-50 ${
                      taskManualToolSelection
                        ? 'bg-blue-900/50 text-blue-400 hover:bg-blue-900/80'
                        : 'hover:bg-gray-300 hover:text-black'
                    }`}
                  >
                    <BsPuzzle className="text-base" />
                  </button>

                  {taskManualToolSelection && (
                    <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-primary_c text-xs text-white">
                      {(taskOqlousTools?.length || 0) +
                        (taskMCPConnections?.length || 0)}
                    </span>
                  )}
                </div>

                {/* Desktop Options */}
                <div className="hidden items-center gap-2 md:flex">
                  <button
                    disabled={sendTaskLoading}
                    onClick={() => {
                      setIsReasoning(!isReasoning);
                    }}
                    className={`flex h-7 items-center gap-1 rounded-full border px-2 transition-all duration-300  disabled:pointer-events-none disabled:opacity-50 ${
                      isReasoning
                        ? 'border-primary_c bg-primary_c text-white'
                        : 'border-zinc-600 bg-transparent hover:bg-zinc-600 hover:text-white'
                    }`}
                  >
                    <FaRegLightbulb className="text-base" />
                    <span className="text-xs">Reason</span>
                  </button>

                  <button
                    disabled={sendTaskLoading}
                    onClick={() => {
                      setIsWebSearch(!isWebSearch);
                    }}
                    className={`flex h-7 items-center gap-1 rounded-full border px-2 transition-all duration-300  disabled:pointer-events-none disabled:opacity-50 ${
                      isWebSearch
                        ? 'border-primary_c bg-primary_c text-white'
                        : 'border-zinc-600 bg-transparent hover:bg-zinc-600 hover:text-white'
                    }`}
                  >
                    <FaGlobeAsia className="text-base" />
                    <span className="text-xs">Web</span>
                  </button>

                  {!IN_PRODUCTION && (
                    <div className="relative ">
                      <button
                        disabled={sendTaskLoading}
                        onClick={handleAgentButtonClick}
                        className={`flex h-7 items-center gap-2 rounded-full border px-3 transition-all duration-300 disabled:pointer-events-none  disabled:opacity-50 ${
                          isAgentMode
                            ? 'border-primary_c bg-primary_c pr-6 text-white'
                            : 'border-zinc-700 bg-transparent hover:bg-zinc-600 hover:text-white'
                        }`}
                      >
                        <BsRobot className="text-base" />
                        <span className="text-xs">Agents</span>
                      </button>

                      {isAgentMode && (
                        <button
                          disabled={sendTaskLoading}
                          onClick={handleTurnOffAgentMode}
                          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-black/20 p-0.5 transition-colors hover:bg-black/40  disabled:pointer-events-none disabled:opacity-50 "
                          title="Turn off Agent Mode"
                        >
                          <RxCross2 size={14} />
                        </button>
                      )}
                    </div>
                  )}

                  {userInfo?.available_models?.length > 0 && (
                    <div className="relative" ref={modelSelectorRef}>
                      <button
                        disabled={sendTaskLoading}
                        onClick={() =>
                          setIsModelSelectorOpen(!isModelSelectorOpen)
                        }
                        title="Select Language Model"
                        className="flex h-7 items-center gap-1.5 rounded-full border border-zinc-700 bg-transparent px-3 transition-all
                      duration-300 hover:bg-zinc-600 hover:text-white disabled:pointer-events-none disabled:opacity-50"
                      >
                        <FiCpu className="text-base" />
                        <span className="max-w-[100px] truncate text-xs">
                          {currentTaskModel || 'Model'}
                        </span>
                        <IoChevronUp
                          className={`text-xs transition-transform duration-200 ${isModelSelectorOpen ? 'rotate-0' : 'rotate-180'}`}
                        />
                      </button>
                      {isModelSelectorOpen && (
                        <div
                          className="absolute bottom-full z-50 mb-3 
    w-72 overflow-hidden rounded-xl 
    border border-zinc-800 bg-zinc-900 
    shadow-2xl backdrop-blur-md"
                        >
                          <ul className="max-h-72 overflow-y-auto py-2">
                            {/* Default Model */}
                            <li>
                              <button
                                onClick={() => {
                                  setCurrentTaskModel(null);
                                  setIsModelSelectorOpen(false);
                                }}
                                className={`flex w-full items-center justify-between px-4 py-2.5
            text-sm transition-all duration-150
            ${
              currentTaskModel === null
                ? 'bg-zinc-800 text-zinc-100'
                : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'
            }`}
                              >
                                <div className="flex items-center gap-3">
                                  <TbBrandOpenai className="text-base text-zinc-400" />

                                  <span className="font-medium tracking-wide">
                                    {userInfo?.default_model?.name || 'Oqlous'}
                                  </span>
                                </div>

                                {currentTaskModel === null && (
                                  <FiCheck className="text-sm text-zinc-300" />
                                )}
                              </button>
                            </li>

                            {/* Divider */}
                            <div className="my-2 border-t border-zinc-800" />

                            {/* Available Models */}
                            {userInfo.available_models.map((model, index) => (
                              <li key={model.name || index}>
                                <button
                                  onClick={() => {
                                    setCurrentTaskModel(model.name);
                                    setIsModelSelectorOpen(false);
                                  }}
                                  className={`flex w-full items-center justify-between px-4 py-2.5
              text-sm transition-all duration-150
              ${
                currentTaskModel === model.name
                  ? 'bg-zinc-800 text-zinc-100'
                  : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200'
              }`}
                                >
                                  <div className="flex items-center gap-3">
                                    {/* Icon via Regex */}
                                    {getModelIcon(model.name)}

                                    <div className="flex items-center gap-2">
                                      <span className="font-medium tracking-wide">
                                        {model.name}
                                      </span>

                                      {/* {model.is_new && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded 
                    bg-zinc-800 border border-zinc-700 
                    text-zinc-400 uppercase tracking-wide">
                                          New
                                        </span>
                                      )} */}
                                    </div>
                                  </div>

                                  {currentTaskModel === model.name && (
                                    <FiCheck className="text-sm text-zinc-300" />
                                  )}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Mobile Tools Toggle */}
                <div className="relative flex-shrink-0 md:hidden">
                  <button
                    onClick={() => setShowMobileOptions(!showMobileOptions)}
                    className={`flex h-7 items-center gap-1 rounded-full border px-2 transition-all duration-300 ${
                      isReasoning ||
                      isWebSearch ||
                      isCanvas ||
                      isAgentMode ||
                      showMobileOptions
                        ? 'border-zinc-700 bg-zinc-700 text-white'
                        : 'border-zinc-500 bg-transparent hover:bg-zinc-600 hover:text-white'
                    }`}
                  >
                    <FiSliders className="text-base" />
                    <span className="text-xs">Tools</span>
                    {(isReasoning ||
                      isWebSearch ||
                      isCanvas ||
                      isAgentMode) && (
                      <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary_c text-xs text-white">
                        {
                          [
                            isReasoning,
                            isWebSearch,
                            isCanvas,
                            isAgentMode,
                          ].filter(Boolean).length
                        }
                      </span>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex-shrink-0">
                {isStreaming ? (
                  // If streaming, show the STOP button
                  <button
                    onClick={stopStreaming}
                    className="flex h-7 w-7 items-center justify-center rounded-lg text-5xl text-white transition-all duration-300"
                    aria-label="Stop generating response"
                  >
                    <LoadingPauseIcon />
                  </button>
                ) : !isBusy &&
                  !isloading &&
                  !createTaskLoading &&
                  !sendSubTaskLoading &&
                  !sendTaskLoading &&
                  !hasPendingToolCalls ? (
                  // If not busy, show the SEND button
                  <button
                    disabled={
                      isloading ||
                      createTaskLoading ||
                      sendTaskLoading ||
                      isBusy ||
                      sendSubTaskLoading ||
                      (toolCallApprovalList?.length || 0) > 0
                    }
                    onClick={() => {
                      // Prepend template context if a template is selected
                      let messageToSend = inputValue;
                      if (selectedTemplate) {
                        messageToSend = `[Using template: "${selectedTemplate.template_name}" (template_id: ${selectedTemplate._id})]\n${inputValue}`;
                        setSelectedTemplate(null);
                      }
                      handleSend(messageToSend);
                      setInputValue('');
                    }}
                    className={`flex h-7 w-7 items-center justify-center rounded-lg text-5xl transition-all duration-300 ${
                      isloading ||
                      createTaskLoading ||
                      sendTaskLoading ||
                      sendSubTaskLoading ||
                      (toolCallApprovalList?.length || 0) > 0
                        ? 'bg-gradient-to-b from-[#5566FF]/50 to-[#333D99]/50'
                        : 'bg-primary-gradient'
                    }`}
                  >
                    <IoSend className="text-base" />
                  </button>
                ) : (
                  // For any other busy state, show the LOADER
                  <div className="flex h-8 w-8 items-center justify-center">
                    <div style={{ transform: 'scale(1.5)' }}>
                      <Loader />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {newChat && (
          <IntegrationGuideNotification
            handleShowIntegration={(e) => {
              e.stopPropagation();
              setShowIntegrations(true);
            }}
          />
        )}
      </div>

      {/* Modals */}
      <>
        {/* The overlay now has two different states */}
        {isDraggingOver &&
          (sendTaskLoading ? (
            // 🎨 BUSY STATE UI
            <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-white bg-yellow-600 bg-opacity-70">
              <FaClock className="mb-3 animate-pulse text-4xl text-white" />
              <h3 className="text-lg font-semibold text-white">
                Task in Progress
              </h3>
              <p className="text-sm text-white">Please wait to upload files.</p>
            </div>
          ) : (
            // ✅ AVAILABLE STATE UI
            <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-white bg-primary_c bg-opacity-50">
              <FaFileUpload className="mb-2 text-4xl text-white" />
              <p className="text-lg font-semibold text-white">
                Drop files here to upload
              </p>
            </div>
          ))}

        {showDropdown && filteredData.length > 0 && (
          <div
            style={{
              position: 'fixed',
              top: 'auto',
              bottom: `${window.innerHeight - dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 9999,
            }}
            className="flex max-h-60 w-fit flex-col justify-end overflow-y-auto"
          >
            <ul className="h-fit min-w-[300px] overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
              {filteredData.map((item, index) =>
                renderDropdownItem(item, index),
              )}
            </ul>
          </div>
        )}

        {/* Mobile Tools Dropup */}
        {showDataSource && (
          <DataSourceModal closeModal={closeDataSourceModal} />
        )}

        {/* Render the Agent Architecture Modal */}
        <AgentArchitectureModal
          isOpen={isAgentModalOpen}
          onClose={() => setIsAgentModalOpen(false)}
        />

        {/* NEW: Render the McpTaskSelector Modal */}
        <McpTaskSelector
          isOpen={isMcpSelectorOpen}
          onClose={() => setIsMcpSelectorOpen(false)}
        />

        <GroupsModal
          isOpen={showGroups}
          onClose={() => setShowGroups(false)}
          openAddGroup={openModal}
        />

        <SelectFunctions
          selectedValue={selectedValue}
          isOpen={false}
          onClose={() => setShowGroups(false)}
        />

        <DataApps
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          setSelectedValue={setSelectedValue}
        />
        <ExcelPreviewModal
          isOpen={excelPreviewModal.isOpen}
          onClose={closeExcelPreview}
          file={excelPreviewModal.file}
        />
        <DocxPreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
        {showIntegrations && (
          <div className="fixed inset-0 z-50">
            <Integration
              onClose={() => setShowIntegrations(false)}
              user_google_id={userGoogleId}
            />
          </div>
        )}
      </>
    </>
  );
};

export default AutoResizableTextarea;
